import { useMemo } from "react";
import type { RoomLayoutRuntime, RoomThemeRuntime } from "../../shared/types";
import {
  getDoorExitCell,
  getTileHeight,
  getTileSurface,
  getWalkableCells,
  isWalkableCell,
  resolveLayoutWalls,
  TILE_WORLD_HEIGHT_UNIT,
} from "../../shared/room-layout";
import { createDefaultRoomThemeRuntime } from "../../shared/room-theme";
import { WALL_HEIGHT } from "../lib/constants";

export const FLOOR_THICKNESS = 0.18;
export const WALL_THICKNESS = 0.22;
const TOP_WALL_DIRECTIONS = new Set(["north", "west"]);
const DOOR_OPENING_HEIGHT = 2.15;
const FLOOR_BLOCK_SIZE = 1.02;
const FLOOR_TOP_SIZE = 0.98;
const PORCH_MARKER_SIZE = 1.04;
const STAIR_STEP_COUNT = 3;
const STAIR_STEP_OVERLAP = 0.025;
const STAIR_TOP_INSET_SCALE = 0.955;
const WALL_SEGMENT_LENGTH = 1.0;
const EXIT_TILE_BLOCK_SIZE = FLOOR_BLOCK_SIZE;
const EXIT_TILE_OUTWARD_OFFSET = 0;

const DEFAULT_THEME = createDefaultRoomThemeRuntime();

interface Props {
  layout: RoomLayoutRuntime;
  theme?: RoomThemeRuntime;
}

interface StairProfile {
  axis: "x" | "z";
  ascendPositive: boolean;
  lowHeight: number;
  highHeight: number;
}

function neighborHeight(
  layout: RoomLayoutRuntime,
  x: number,
  z: number,
  dx: number,
  dz: number
): number | null {
  const nx = x + dx;
  const nz = z + dz;
  if (!isWalkableCell(layout, nx, nz)) {
    return null;
  }
  return getTileHeight(layout, nx, nz);
}

function resolveStairProfile(
  layout: RoomLayoutRuntime,
  x: number,
  z: number,
  tileHeight: number
): StairProfile {
  const north = neighborHeight(layout, x, z, 0, -1);
  const south = neighborHeight(layout, x, z, 0, 1);
  const east = neighborHeight(layout, x, z, 1, 0);
  const west = neighborHeight(layout, x, z, -1, 0);

  const hasXAxis = east !== null || west !== null;
  const hasZAxis = north !== null || south !== null;
  let axis: "x" | "z" = "x";
  let ascendPositive = true;

  if (hasXAxis && hasZAxis) {
    const deltaX = (east ?? tileHeight) - (west ?? tileHeight);
    const deltaZ = (south ?? tileHeight) - (north ?? tileHeight);
    if (Math.abs(deltaZ) > Math.abs(deltaX)) {
      axis = "z";
      ascendPositive = deltaZ >= 0;
    } else {
      axis = "x";
      ascendPositive = deltaX >= 0;
    }
  } else if (hasZAxis) {
    axis = "z";
    ascendPositive = (south ?? tileHeight) >= (north ?? tileHeight);
  } else {
    axis = "x";
    ascendPositive = (east ?? tileHeight) >= (west ?? tileHeight);
  }

  let lowHeight = tileHeight - 0.5;
  let highHeight = tileHeight + 0.5;
  if (axis === "x") {
    if (ascendPositive) {
      lowHeight = west ?? lowHeight;
      highHeight = east ?? highHeight;
    } else {
      lowHeight = east ?? lowHeight;
      highHeight = west ?? highHeight;
    }
  } else if (ascendPositive) {
    lowHeight = north ?? lowHeight;
    highHeight = south ?? highHeight;
  } else {
    lowHeight = south ?? lowHeight;
    highHeight = north ?? highHeight;
  }

  if (highHeight < lowHeight) {
    const swap = lowHeight;
    lowHeight = highHeight;
    highHeight = swap;
  }
  if (highHeight - lowHeight < 0.25) {
    lowHeight = tileHeight - 0.5;
    highHeight = tileHeight + 0.5;
  }

  return {
    axis,
    ascendPositive,
    lowHeight,
    highHeight,
  };
}

export function IsometricRoom({ layout, theme }: Props) {
  const colors = theme ?? DEFAULT_THEME;
  const walkableCells = useMemo(() => getWalkableCells(layout), [layout]);
  const [exitX, exitZ] = useMemo(() => getDoorExitCell(layout), [layout]);
  const exitHeightY = useMemo(
    () =>
      getTileHeight(layout, layout.door.x, layout.door.z) *
      TILE_WORLD_HEIGHT_UNIT,
    [layout]
  );
  const exitVisualOffset = useMemo<[number, number]>(() => {
    switch (layout.door.direction) {
      case "north":
        return [0, -EXIT_TILE_OUTWARD_OFFSET];
      case "south":
        return [0, EXIT_TILE_OUTWARD_OFFSET];
      case "west":
        return [-EXIT_TILE_OUTWARD_OFFSET, 0];
      case "east":
        return [EXIT_TILE_OUTWARD_OFFSET, 0];
      default:
        return [0, 0];
    }
  }, [layout.door.direction]);
  const boundaryEdges = useMemo(() => {
    return resolveLayoutWalls(layout).filter((edge) =>
      TOP_WALL_DIRECTIONS.has(edge.direction)
    );
  }, [layout]);

  const cornerCaps = useMemo(() => {
    const northCells = new Set<string>();
    const westCells = new Set<string>();
    for (const edge of boundaryEdges) {
      const key = `${edge.x},${edge.z}`;
      if (edge.direction === "north") {
        northCells.add(key);
      } else if (edge.direction === "west") {
        westCells.add(key);
      }
    }
    const corners: Array<{ x: number; z: number; y: number }> = [];
    for (const key of northCells) {
      if (!westCells.has(key)) continue;
      const [xText, zText] = key.split(",");
      const x = Number.parseInt(xText, 10);
      const z = Number.parseInt(zText, 10);
      const tileH = getTileHeight(layout, x, z);
      const tileSurf = getTileSurface(layout, x, z);
      const effectiveBaseH =
        tileSurf === "stairs"
          ? resolveStairProfile(layout, x, z, tileH).lowHeight
          : tileH;
      corners.push({
        x,
        z,
        y: effectiveBaseH * TILE_WORLD_HEIGHT_UNIT,
      });
    }
    return corners;
  }, [boundaryEdges, layout]);

  const topWallEdges = useMemo(() => {
    return boundaryEdges.filter(
      (edge) =>
        !(
          edge.x === layout.door.x &&
          edge.z === layout.door.z &&
          edge.direction === layout.door.direction
        )
    );
  }, [boundaryEdges, layout]);
  const hasDoorWall = useMemo(
    () =>
      boundaryEdges.some(
        (edge) =>
          edge.x === layout.door.x &&
          edge.z === layout.door.z &&
          edge.direction === layout.door.direction
      ),
    [boundaryEdges, layout]
  );
  const topWallEdgeSet = useMemo(
    () =>
      new Set(
        topWallEdges.map((edge) => `${edge.x},${edge.z},${edge.direction}`)
      ),
    [topWallEdges]
  );
  const wallTopY = useMemo(() => {
    let maxBaseY = 0;
    for (const edge of boundaryEdges) {
      const baseY =
        getTileHeight(layout, edge.x, edge.z) * TILE_WORLD_HEIGHT_UNIT;
      if (baseY > maxBaseY) {
        maxBaseY = baseY;
      }
    }
    return maxBaseY + WALL_HEIGHT;
  }, [boundaryEdges, layout]);

  const WT = WALL_THICKNESS;
  const FT = FLOOR_THICKNESS;

  return (
    <group>
      {walkableCells.map(([x, z]) => {
        const tileHeightY =
          getTileHeight(layout, x, z) * TILE_WORLD_HEIGHT_UNIT;
        const tileSurface = getTileSurface(layout, x, z) ?? "floor";
        if (tileSurface === "stairs") {
          const profile = resolveStairProfile(
            layout,
            x,
            z,
            getTileHeight(layout, x, z)
          );
          const lowY = profile.lowHeight * TILE_WORLD_HEIGHT_UNIT;
          const highY = profile.highHeight * TILE_WORLD_HEIGHT_UNIT;
          const rise = Math.max(TILE_WORLD_HEIGHT_UNIT * 0.25, highY - lowY);
          // Keep one stair increment of vertical gap at low/high landings.
          // This makes the first stair from the high side start one step below the high floor.
          const stepRise = rise / (STAIR_STEP_COUNT + 1);
          const treadThickness = Math.max(
            0.045,
            Math.min(FT * 0.92, stepRise * 0.72)
          );
          return (
            <group key={`floor-${x}-${z}`}>
              {Array.from({ length: STAIR_STEP_COUNT }).map((_, index) => {
                const startT = index / STAIR_STEP_COUNT;
                const endT = (index + 1) / STAIR_STEP_COUNT;
                const stepBaseY = lowY + stepRise * index;
                const stepTopY = lowY + stepRise * (index + 1);
                const alongLow = profile.ascendPositive
                  ? -0.5 + startT
                  : 0.5 - startT;
                const alongHigh = profile.ascendPositive
                  ? -0.5 + endT
                  : 0.5 - endT;
                const stepAlongCenter = (alongLow + alongHigh) / 2;
                const stepAlongSize = Math.max(
                  0.08,
                  Math.abs(alongHigh - alongLow) + STAIR_STEP_OVERLAP
                );
                const isXAxis = profile.axis === "x";
                const stepBodyHeight = Math.max(0.03, stepTopY - stepBaseY);

                return (
                  <group key={`stair-step-${x}-${z}-${index}`}>
                    <mesh
                      receiveShadow
                      position={
                        isXAxis
                          ? [
                              x + stepAlongCenter,
                              stepBaseY + stepBodyHeight / 2,
                              z,
                            ]
                          : [
                              x,
                              stepBaseY + stepBodyHeight / 2,
                              z + stepAlongCenter,
                            ]
                      }
                    >
                      <boxGeometry
                        args={
                          isXAxis
                            ? [stepAlongSize, stepBodyHeight, FLOOR_BLOCK_SIZE]
                            : [FLOOR_BLOCK_SIZE, stepBodyHeight, stepAlongSize]
                        }
                      />
                      <meshStandardMaterial
                        color={colors.floorSide}
                        roughness={0.82}
                      />
                    </mesh>
                    <mesh
                      receiveShadow
                      rotation={[-Math.PI / 2, 0, 0]}
                      position={
                        isXAxis
                          ? [x + stepAlongCenter, stepTopY + 0.003, z]
                          : [x, stepTopY + 0.003, z + stepAlongCenter]
                      }
                    >
                      <planeGeometry
                        args={
                          isXAxis
                            ? [
                                stepAlongSize * STAIR_TOP_INSET_SCALE,
                                FLOOR_TOP_SIZE * STAIR_TOP_INSET_SCALE,
                              ]
                            : [
                                FLOOR_TOP_SIZE * STAIR_TOP_INSET_SCALE,
                                stepAlongSize * STAIR_TOP_INSET_SCALE,
                              ]
                        }
                      />
                      <meshStandardMaterial
                        color={colors.floorTop}
                        roughness={0.78}
                        metalness={0.01}
                      />
                    </mesh>
                  </group>
                );
              })}
            </group>
          );
        }

        return (
          <group key={`floor-${x}-${z}`}>
            <mesh receiveShadow position={[x, tileHeightY - FT / 2, z]}>
              <boxGeometry args={[FLOOR_BLOCK_SIZE, FT, FLOOR_BLOCK_SIZE]} />
              <meshStandardMaterial color={colors.floorSide} roughness={0.82} />
            </mesh>
            <mesh
              receiveShadow
              rotation={[-Math.PI / 2, 0, 0]}
              position={[x, tileHeightY + 0.003, z]}
            >
              <planeGeometry args={[FLOOR_TOP_SIZE, FLOOR_TOP_SIZE]} />
              <meshStandardMaterial
                color={colors.floorTop}
                roughness={0.76}
                metalness={0.01}
              />
            </mesh>
          </group>
        );
      })}

      <group>
        <mesh
          position={[
            exitX + exitVisualOffset[0],
            exitHeightY - FT / 2,
            exitZ + exitVisualOffset[1],
          ]}
        >
          <boxGeometry args={[EXIT_TILE_BLOCK_SIZE, FT, EXIT_TILE_BLOCK_SIZE]} />
          <meshStandardMaterial color={colors.floorSide} roughness={0.82} />
        </mesh>
        <mesh
          rotation={[-Math.PI / 2, 0, 0]}
          position={[
            exitX + exitVisualOffset[0],
            exitHeightY + 0.003,
            exitZ + exitVisualOffset[1],
          ]}
        >
          <planeGeometry args={[FLOOR_TOP_SIZE, FLOOR_TOP_SIZE]} />
          <meshStandardMaterial
            color={colors.floorTop}
            roughness={0.78}
            metalness={0.01}
          />
        </mesh>
        <mesh
          rotation={[-Math.PI / 2, 0, 0]}
          position={[
            exitX + exitVisualOffset[0],
            exitHeightY + 0.004,
            exitZ + exitVisualOffset[1],
          ]}
        >
          <planeGeometry args={[PORCH_MARKER_SIZE, PORCH_MARKER_SIZE]} />
          <meshStandardMaterial
            color={colors.porchMarker}
            roughness={0.92}
            transparent
            opacity={0.22}
          />
        </mesh>
      </group>

      {topWallEdges.map((edge) => {
        const key = `wall-${edge.x}-${edge.z}-${edge.direction}`;
        const tileH = getTileHeight(layout, edge.x, edge.z);
        const tileSurf = getTileSurface(layout, edge.x, edge.z);
        // For stair tiles, anchor the wall base at the low end of the
        // staircase so wall bottoms align cleanly with the stair start.
        const effectiveBaseH =
          tileSurf === "stairs"
            ? resolveStairProfile(layout, edge.x, edge.z, tileH).lowHeight
            : tileH;
        const wallBaseY = effectiveBaseH * TILE_WORLD_HEIGHT_UNIT;
        const wallHeight = Math.max(0.24, wallTopY - wallBaseY);
        if (edge.direction === "north") {
          const wallZ = edge.z - 0.5 - WT / 2;
          const hasLeftNeighbor = topWallEdgeSet.has(
            `${edge.x - 1},${edge.z},north`
          );
          const hasRightNeighbor = topWallEdgeSet.has(
            `${edge.x + 1},${edge.z},north`
          );
          const hasLeftCornerJoin = topWallEdgeSet.has(
            `${edge.x},${edge.z},west`
          );
          const hasRightCornerJoin = topWallEdgeSet.has(
            `${edge.x + 1},${edge.z},west`
          );
          return (
            <group key={key}>
              <mesh
                position={[edge.x, wallBaseY + wallHeight / 2, wallZ]}
                castShadow
                receiveShadow
              >
                <boxGeometry args={[WALL_SEGMENT_LENGTH, wallHeight, WT]} />
                <meshStandardMaterial color={colors.wall} roughness={0.9} />
              </mesh>
              <mesh
                receiveShadow
                position={[edge.x, wallBaseY - FT / 2, wallZ]}
              >
                <boxGeometry args={[FLOOR_BLOCK_SIZE, FT, WT]} />
                <meshStandardMaterial color={colors.floorSide} roughness={0.82} />
              </mesh>
              {!hasLeftNeighbor && !hasLeftCornerJoin && (
                <mesh
                  position={[
                    edge.x - WALL_SEGMENT_LENGTH / 2 + WT / 2,
                    wallBaseY + wallHeight / 2,
                    wallZ,
                  ]}
                  castShadow
                  receiveShadow
                >
                  <boxGeometry args={[WT, wallHeight, WT]} />
                  <meshStandardMaterial color={colors.wall} roughness={0.9} />
                </mesh>
              )}
              {!hasRightNeighbor && !hasRightCornerJoin && (
                <mesh
                  position={[
                    edge.x + WALL_SEGMENT_LENGTH / 2 - WT / 2,
                    wallBaseY + wallHeight / 2,
                    wallZ,
                  ]}
                  castShadow
                  receiveShadow
                >
                  <boxGeometry args={[WT, wallHeight, WT]} />
                  <meshStandardMaterial color={colors.wall} roughness={0.9} />
                </mesh>
              )}
            </group>
          );
        }
        if (edge.direction !== "west") {
          return null;
        }
        const wallX = edge.x - 0.5 - WT / 2;
        const hasTopNeighbor = topWallEdgeSet.has(
          `${edge.x},${edge.z - 1},west`
        );
        const hasBottomNeighbor = topWallEdgeSet.has(
          `${edge.x},${edge.z + 1},west`
        );
        const hasTopCornerJoin = topWallEdgeSet.has(
          `${edge.x},${edge.z},north`
        );
        const hasBottomCornerJoin = topWallEdgeSet.has(
          `${edge.x},${edge.z + 1},north`
        );
        return (
          <group key={key}>
            <mesh
              position={[wallX, wallBaseY + wallHeight / 2, edge.z]}
              castShadow
              receiveShadow
            >
              <boxGeometry args={[WT, wallHeight, WALL_SEGMENT_LENGTH]} />
              <meshStandardMaterial color={colors.wall} roughness={0.9} />
            </mesh>
            <mesh receiveShadow position={[wallX, wallBaseY - FT / 2, edge.z]}>
              <boxGeometry args={[WT, FT, FLOOR_BLOCK_SIZE]} />
              <meshStandardMaterial color={colors.floorSide} roughness={0.82} />
            </mesh>
            {!hasTopNeighbor && !hasTopCornerJoin && (
              <mesh
                position={[
                  wallX,
                  wallBaseY + wallHeight / 2,
                  edge.z - WALL_SEGMENT_LENGTH / 2 + WT / 2,
                ]}
                castShadow
                receiveShadow
              >
                <boxGeometry args={[WT, wallHeight, WT]} />
                <meshStandardMaterial color={colors.wall} roughness={0.9} />
              </mesh>
            )}
            {!hasBottomNeighbor && !hasBottomCornerJoin && (
              <mesh
                position={[
                  wallX,
                  wallBaseY + wallHeight / 2,
                  edge.z + WALL_SEGMENT_LENGTH / 2 - WT / 2,
                ]}
                castShadow
                receiveShadow
              >
                <boxGeometry args={[WT, wallHeight, WT]} />
                <meshStandardMaterial color={colors.wall} roughness={0.9} />
              </mesh>
            )}
          </group>
        );
      })}

      {cornerCaps.map((corner) => {
        const cornerHeight = Math.max(0.24, wallTopY - corner.y);
        return (
          <group key={`corner-${corner.x}-${corner.z}`}>
            <mesh
              receiveShadow
              position={[
                corner.x - 0.5 - WT / 2,
                corner.y - FT / 2,
                corner.z - 0.5 - WT / 2,
              ]}
            >
              <boxGeometry args={[WT, FT, WT]} />
              <meshStandardMaterial color={colors.floorSide} roughness={0.82} />
            </mesh>
            <mesh
              position={[
                corner.x - 0.5 - WT / 2,
                corner.y + cornerHeight / 2,
                corner.z - 0.5 - WT / 2,
              ]}
              castShadow
              receiveShadow
            >
              <boxGeometry args={[WT, cornerHeight, WT]} />
              <meshStandardMaterial color={colors.wall} roughness={0.9} />
            </mesh>
          </group>
        );
      })}

      {layout.door.direction === "north" && hasDoorWall && (
        <mesh
          position={[
            layout.door.x,
            getTileHeight(layout, layout.door.x, layout.door.z) *
              TILE_WORLD_HEIGHT_UNIT +
              DOOR_OPENING_HEIGHT +
              Math.max(
                0.24,
                wallTopY -
                  (getTileHeight(layout, layout.door.x, layout.door.z) *
                    TILE_WORLD_HEIGHT_UNIT +
                    DOOR_OPENING_HEIGHT)
              ) /
                2,
            layout.door.z - 0.5 - WT / 2,
          ]}
        >
          <boxGeometry
            args={[
              WALL_SEGMENT_LENGTH,
              Math.max(
                0.24,
                wallTopY -
                  (getTileHeight(layout, layout.door.x, layout.door.z) *
                    TILE_WORLD_HEIGHT_UNIT +
                    DOOR_OPENING_HEIGHT)
              ),
              WT,
            ]}
          />
          <meshStandardMaterial color={colors.wall} roughness={0.9} />
        </mesh>
      )}

      {layout.door.direction === "west" && hasDoorWall && (
        <mesh
          position={[
            layout.door.x - 0.5 - WT / 2,
            getTileHeight(layout, layout.door.x, layout.door.z) *
              TILE_WORLD_HEIGHT_UNIT +
              DOOR_OPENING_HEIGHT +
              Math.max(
                0.24,
                wallTopY -
                  (getTileHeight(layout, layout.door.x, layout.door.z) *
                    TILE_WORLD_HEIGHT_UNIT +
                    DOOR_OPENING_HEIGHT)
              ) /
                2,
            layout.door.z,
          ]}
        >
          <boxGeometry
            args={[
              WT,
              Math.max(
                0.24,
                wallTopY -
                  (getTileHeight(layout, layout.door.x, layout.door.z) *
                    TILE_WORLD_HEIGHT_UNIT +
                    DOOR_OPENING_HEIGHT)
              ),
              WALL_SEGMENT_LENGTH,
            ]}
          />
          <meshStandardMaterial color={colors.wall} roughness={0.9} />
        </mesh>
      )}
    </group>
  );
}
